import React, { useState, useEffect } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import CrmKpiCards from './crm/components/CrmKpiCards';
import CrmQuickActions from './crm/components/CrmQuickActions';
import CrmWeeklyActivityChart from './crm/components/CrmWeeklyActivityChart';
import CrmModuleBreakdownChart from './crm/components/CrmModuleBreakdownChart';
import CrmPipelineChart from './crm/components/CrmPipelineChart';
import CrmRecentActivity from './crm/components/CrmRecentActivity';
import CrmTasksDue from './crm/components/CrmTasksDue';

// Mock data for charts and activity
const activityData = [
  {
    date: 'Mon', Contacts: 5, Properties: 2, Tasks: 1, Notes: 3,
  },
  {
    date: 'Tue', Contacts: 8, Properties: 1, Tasks: 2, Notes: 2,
  },
  {
    date: 'Wed', Contacts: 6, Properties: 3, Tasks: 3, Notes: 4,
  },
  {
    date: 'Thu', Contacts: 10, Properties: 2, Tasks: 4, Notes: 5,
  },
  {
    date: 'Fri', Contacts: 7, Properties: 4, Tasks: 2, Notes: 3,
  },
  {
    date: 'Sat', Contacts: 4, Properties: 1, Tasks: 1, Notes: 2,
  },
  {
    date: 'Sun', Contacts: 3, Properties: 0, Tasks: 1, Notes: 1,
  },
];

const pipelineData = [
  { stage: 'New', count: 8 },
  { stage: 'Qualified', count: 5 },
  { stage: 'Proposal', count: 3 },
  { stage: 'Negotiation', count: 2 },
  { stage: 'Closed Won', count: 4 },
  { stage: 'Closed Lost', count: 1 },
];

const recentTasks = [
  { title: 'Call Jane Smith', due: '2024-07-17', status: 'Pending' },
  { title: 'Schedule viewing for Oak Ave', due: '2024-07-16', status: 'Completed' },
  { title: 'Send contract to Alice Lee', due: '2024-07-15', status: 'Pending' },
  { title: 'Follow up with Beta LLC', due: '2024-07-14', status: 'Pending' },
  { title: 'Prepare proposal for Gamma Inc', due: '2024-07-13', status: 'Pending' },
];

const recentActivity = [
  {
    type: 'contact', description: 'Added new contact Jane Smith', date: '2024-07-17', user: 'Adeel',
  },
  {
    type: 'deal', description: 'Deal moved to Proposal stage', date: '2024-07-17', user: 'Prakash',
  },
  {
    type: 'task', description: 'Task completed: Call Jane', date: '2024-07-16', user: 'Aatif',
  },
  {
    type: 'note', description: 'Note added to Beta LLC', date: '2024-07-16', user: 'Jannat',
  },
  {
    type: 'notification', description: 'New notification: Contract signed', date: '2024-07-15', user: 'System',
  },
];

function CrmDashboard() {
  const navigate = useNavigate();
  const [telemetry, setTelemetry] = useState(null);

  // Example stats, replace with real data if available
  const crmStats = {
    contacts: 42,
    properties: 12,
    tasks: 7,
    notes: 18,
    leadStages: 5,
    notifications: 3,
    aiFields: 9,
  };

  useEffect(() => {
    // Load telemetry metrics for CRM dashboard
    fetch('/api/v1/telemetry/app', {
      credentials: 'include',
      headers: { Authorization: `Bearer ${localStorage.getItem('tkn')}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setTelemetry(data))
      .catch(() => setTelemetry(null));
  }, []);

  // Handlers for quick actions (to be implemented)
  const handleAddContact = () => navigate('/crm/contacts/new');
  const handleAddDeal = () => navigate('/crm/opportunities/new');
  const handleAddTask = () => navigate('/crm/tasks/new');
  const handleAddNote = () => navigate('/crm/notes/new');

  return (
    <Box sx={{
      p: { xs: 2, md: 4 }, height: '100%', bgcolor: 'background.default', overflow: 'auto',
    }}
    >
      <Typography variant="h4" fontWeight={700} gutterBottom>
        CRM Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Visualize your CRM activity, recent records, and trends.
      </Typography>

      {/* KPI Cards */}
      <CrmKpiCards stats={crmStats} />

      {/* Quick Actions */}
      <CrmQuickActions
        onAddContact={handleAddContact}
        onAddDeal={handleAddDeal}
        onAddTask={handleAddTask}
        onAddNote={handleAddNote}
      />

      {/* Charts Section: Weekly Activity + Module Breakdown */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <CrmWeeklyActivityChart activityData={activityData} />
        </Grid>
        <Grid item xs={12} md={4}>
          <CrmModuleBreakdownChart stats={crmStats} />
        </Grid>
      </Grid>

      {/* Pipeline Chart (full width) + Tasks Due */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <CrmPipelineChart pipelineData={pipelineData} />
        </Grid>
        <Grid item xs={12} md={4}>
          <CrmTasksDue tasks={recentTasks} />
        </Grid>
      </Grid>

      {/* Recent Activity Timeline */}
      <CrmRecentActivity activityData={recentActivity} />
    </Box>
  );
}

export default CrmDashboard;
